import { AppNavParams } from '@euler/app/Routes';
import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { withConfirmation } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import {
  CardStyleInterpolators,
  StackNavigationProp,
} from '@react-navigation/stack';
import { ChevronLeft, Play, Trash2, X } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  Image,
  Platform,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type ContinuousCapturePreviewItem = {
  id: string;
  type: 'photo' | 'video';
  thumbnailUri: string;
  mediaUri: string;
};

const kColumns = 3;

export const ContinuousCapturePreviewScreen = wrapNavigatorScreen(
  ({
    items: initialItems,
    onRemove,
    onFinish,
  }: {
    items: ContinuousCapturePreviewItem[];
    onRemove?: (id: string) => void;
    onFinish?: () => void;
  }) => {
    const isMobile = useIsMobileLayout();
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
    const { width: screenW, height: screenH } = useWindowDimensions();

    // Mirror parent's snapshot locally so the grid removes feel instant; the
    // camera screen receives each removal via the onRemove callback.
    const [items, setItems] = useState(initialItems);

    const photoUris = useMemo(
      () => items.filter(x => x.type === 'photo').map(x => x.mediaUri),
      [items],
    );
    const [photoViewerIndex, setPhotoViewerIndex] = useState<
      number | undefined
    >(undefined);

    const onClose = useCallback(() => {
      navigation.goBack();
    }, [navigation]);

    const onItemPress = useCallback(
      (item: ContinuousCapturePreviewItem) => {
        if (item.type === 'video') {
          navigation.navigate('_mediaPreview', {
            type: 'video',
            url: item.mediaUri,
          });
          return;
        }
        const index = photoUris.indexOf(item.mediaUri);
        if (index >= 0) {
          setPhotoViewerIndex(index);
        }
      },
      [navigation, photoUris],
    );

    const onDismissPhotoViewer = useCallback(() => {
      setPhotoViewerIndex(undefined);
    }, []);

    const onItemRemove = usePersistFn(async (id: string) => {
      const ok = await withConfirmation({
        title: '确认',
        message: '是否删除该照片?',
      });
      if (!ok) return;
      setItems(prev => prev.filter(x => x.id !== id));
      onRemove?.(id);
    });

    const handleFinish = useCallback(() => {
      navigation.goBack();
      onFinish?.();
    }, [navigation, onFinish]);

    const cardWidth = isMobile ? screenW : Math.min(rpx(560), screenW * 0.8);
    const cardHeight = isMobile ? screenH : Math.min(rpx(680), screenH * 0.85);
    const innerPadding = rpx(12);
    const cellGap = rpx(8);
    const cellSize =
      (cardWidth - innerPadding * 2 - cellGap * (kColumns - 1)) / kColumns;

    const renderItem = useCallback(
      ({ item }: { item: ContinuousCapturePreviewItem }) => (
        <GridCell
          item={item}
          size={cellSize}
          onPress={onItemPress}
          onRemove={onItemRemove}
        />
      ),
      [cellSize, onItemPress, onItemRemove],
    );

    const ListEmpty = useMemo(
      () => (
        <Center
          style={{
            height: cardHeight - rpx(120),
          }}
        >
          <Typography.Label color="#999" size={rpx(14)}>
            暂无连拍内容
          </Typography.Label>
        </Center>
      ),
      [cardHeight, rpx],
    );

    return (
      <View
        style={{
          flex: 1,
          backgroundColor: isMobile ? '#fff' : 'rgba(0,0,0,0.55)',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
        }}
      >
        <View
          style={{
            width: cardWidth,
            height: cardHeight,
            backgroundColor: '#fff',
            borderRadius: isMobile ? 0 : rpx(12),
            overflow: 'hidden',
            paddingTop: isMobile ? insets.top : 0,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: rpx(15),
              height: rpx(48),
            }}
          >
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ left: 10, top: 10, right: 10, bottom: 10 }}
              style={{
                width: rpx(32),
                height: rpx(32),
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isMobile ? (
                <ChevronLeft size={rpx(22)} color="#333" />
              ) : (
                <X size={rpx(20)} color="#333" />
              )}
            </TouchableOpacity>
            <Typography.Label medium size={rpx(16)} color="#333">
              {`连拍预览 (${items.length})`}
            </Typography.Label>
            {items.length > 0 ? (
              <TouchableOpacity
                onPress={handleFinish}
                hitSlop={{ left: 10, top: 10, right: 10, bottom: 10 }}
                style={{
                  paddingHorizontal: rpx(12),
                  height: rpx(28),
                  borderRadius: rpx(14),
                  backgroundColor: '#07c160',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography.Label color="#fff" size={rpx(13)} bold>
                  完成
                </Typography.Label>
              </TouchableOpacity>
            ) : (
              <View style={{ width: rpx(32) }} />
            )}
          </View>

          <FlatList
            data={items}
            keyExtractor={item => item.id}
            renderItem={renderItem}
            numColumns={kColumns}
            ListEmptyComponent={ListEmpty}
            columnWrapperStyle={{ gap: cellGap }}
            contentContainerStyle={{
              padding: innerPadding,
              gap: cellGap,
              paddingBottom: insets.bottom + rpx(24),
            }}
          />
        </View>
        <ImageViewer
          show={photoViewerIndex != null}
          images={photoUris}
          index={photoViewerIndex ?? 0}
          shareButton={false}
          onDismiss={onDismissPhotoViewer}
        />
      </View>
    );
  },
  {
    headerShown: false,
    presentation: 'transparentModal',
    cardOverlayEnabled: false,
    cardStyleInterpolator:
      Platform.OS === 'ios' ? CardStyleInterpolators.forVerticalIOS : undefined,
  },
);

const GridCell = ({
  item,
  size,
  onPress,
  onRemove,
}: {
  item: ContinuousCapturePreviewItem;
  size: number;
  onPress: (item: ContinuousCapturePreviewItem) => void;
  onRemove: (id: string) => void;
}) => {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ width: size, height: size }}>
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => onPress(item)}
        style={{
          width: size,
          height: size,
          borderRadius: rpx(6),
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Image
          source={{ uri: item.thumbnailUri }}
          resizeMode="cover"
          style={{ width: '100%', height: '100%' }}
        />
        {item.type === 'video' && (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <View
              style={{
                width: size * 0.32,
                height: size * 0.32,
                borderRadius: size * 0.16,
                backgroundColor: 'rgba(0,0,0,0.55)',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Play
                size={size * 0.16}
                color="#fff"
                fill="#fff"
                style={{ marginLeft: size * 0.02 }}
              />
            </View>
          </View>
        )}
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => onRemove(item.id)}
        hitSlop={{ left: 8, top: 8, right: 8, bottom: 8 }}
        style={{
          position: 'absolute',
          right: rpx(4),
          top: rpx(4),
          width: rpx(22),
          height: rpx(22),
          borderRadius: rpx(11),
          backgroundColor: 'rgba(0,0,0,0.55)',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Trash2 size={rpx(12)} color="#fff" />
      </TouchableOpacity>
    </View>
  );
};
